import { OrderTrackingShipmentStatus } from "@redotech/order-tracking-common/order-tracking-types";
import {
  DEFAULT_ITEMS_OVERFLOW_CUE,
  ItemsOverflowCue,
  TrackingPageItemsInFulfillmentBlock,
} from "@redotech/order-tracking-common/tracking-page-definition";
import type {
  TrackingPageLineItem,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { memo, useRef } from "react";
import { useParams } from "react-router-dom";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { Flex } from "../../flex";
import { ExternalLink } from "../../link";
import { LineItem } from "../../settings-elements/order/order-details";
import { Text } from "../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import {
  getBadgeStyles,
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
  getHeaderStyles,
} from "./shared/block-styles";
import { BlockOverflowCue, BlockWrapper } from "./shared/block-wrapper";
import {
  filterVisibleLineItems,
  getSelectedShipmentWithFallback,
  getShipmentIdFromLocation,
  getShipmentIndex,
  getUnfulfilledLineItems,
} from "./shared/fulfillment-utils";
import { useNavigateToShipment } from "./shared/use-navigate-to-shipment";

export const ItemsInFulfillment = memo(function ItemsInFulfillment({
  title,
  imageAspectRatio,
  hidePrice,
  hideQuantity,
  hideVirtualItems,
  hideZeroDollarItems,
  hideBundleComponents,
  showCustomFields,
  customFields,
  lineItemPropertySettings,
  itemsInPackageTextColor,
  itemsInPackageBackgroundColor,
  itemsInPackageBorderRadius,
  overflowCue,
  overflowScrollbarColor,
  showItemCount,
  buttonSettings,
  showReturnsDeeplink,
  returnsDeeplinkTextColor,
  returnsDeeplinkFontSize,
  showClaimsDeeplink,
  claimsDeeplinkTextColor,
  claimsDeeplinkFontSize,
}: TrackingPageItemsInFulfillmentBlock) {
  const visibleLineItems = (items: TrackingPageLineItem[]) =>
    filterVisibleLineItems(items, {
      hideVirtualItems,
      hideZeroDollarItems,
      hideBundleComponents,
    });
  const { order, environment, pageSettings } =
    useRequiredContext(TrackingPageContext);
  const { t } = useTranslation();
  const navigateToShipment = useNavigateToShipment();
  const contentRef = useRef<HTMLDivElement>(null);

  const { fulfillmentIndex: paramsFulfillmentIndex } = useParams<{
    fulfillmentIndex?: string;
  }>();
  const shipmentIndex = getShipmentIndex(
    paramsFulfillmentIndex,
    environment,
    order,
    getShipmentIdFromLocation(),
  );

  const handleNavigate = (i: number) => {
    navigateToShipment(i);
    // Reset the card's internal scroll position to the top
    const scrollableParent =
      contentRef.current?.closest<HTMLElement>("[data-scroll-container]") ??
      contentRef.current?.parentElement;
    if (scrollableParent) {
      scrollableParent.scrollTop = 0;
    }
  };

  const selectedShipment = getSelectedShipmentWithFallback(
    order?.shipments,
    shipmentIndex,
  );

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const badgeStyles = getBadgeStyles(pageSettings);
  const buttonStyles = getButtonStyles(
    pageSettings?.pageButtonSettings,
    buttonSettings,
  );
  const buttonHierarchy = getButtonHierarchy(buttonSettings);
  const buttonBorderRadius = getButtonBorderRadius(pageSettings);
  const isLoading = !order || !imageAspectRatio;

  // Use configurable items in package styling or fall back to defaults
  const effectiveItemsInPackageBorderRadius =
    itemsInPackageBorderRadius !== undefined
      ? itemsInPackageBorderRadius
      : pageSettings?.blockSettings.borderRadius;
  const itemsInPackageBackgroundStyle = itemsInPackageBackgroundColor
    ? { backgroundColor: itemsInPackageBackgroundColor }
    : {};
  const itemsInPackageTextStyle = itemsInPackageTextColor
    ? { ...headerStyles, color: itemsInPackageTextColor }
    : headerStyles;
  const blockOverflowCue = toBlockOverflowCue(
    overflowCue ?? DEFAULT_ITEMS_OVERFLOW_CUE,
    overflowScrollbarColor,
  );
  const itemCount = (count: number) =>
    !showItemCount ? null : (
      <Text
        data-testid="items-count"
        fontSize="sm"
        style={{ ...bodyStyles, opacity: 0.75, whiteSpace: "nowrap" }}
      >
        {count} {t("item", { count })}
      </Text>
    );

  return (
    <BlockWrapper
      environment={environment}
      loading={isLoading}
      overflowCue={blockOverflowCue}
      pageSettings={pageSettings}
      withBorder
      withScrollbar
    >
      {order && imageAspectRatio && (
        <Flex dir="column" flex={1} ref={contentRef}>
          {selectedShipment !== undefined ? (
            <>
              {(() => {
                const thisPackageItems = visibleLineItems(
                  selectedShipment.lineItems,
                );
                if (thisPackageItems.length === 0) {
                  return null;
                }
                return (
                  <Flex dir="column" gap="none" p="xl">
                    <Flex
                      dir="column"
                      gap="none"
                      p="xl"
                      style={{
                        ...itemsInPackageBackgroundStyle,
                        ...(effectiveItemsInPackageBorderRadius !== undefined
                          ? {
                              borderRadius: `${effectiveItemsInPackageBorderRadius}px`,
                            }
                          : {}),
                      }}
                    >
                      <Flex align="center" justify="space-between">
                        <Flex align="baseline" gap="sm">
                          <Text
                            fontSize="lg"
                            fontWeight="semibold"
                            style={itemsInPackageTextStyle}
                          >
                            {title ?? t("Items in this package")}
                          </Text>
                          {itemCount(thisPackageItems.length)}
                        </Flex>
                        <Flex align="center" gap="md">
                          {showReturnsDeeplink &&
                            (order.returnDeeplink ||
                              environment ===
                                TrackingPageEnvironment.BUILDER) && (
                              <ExternalLink
                                style={{
                                  color: returnsDeeplinkTextColor || "inherit",
                                  fontSize: returnsDeeplinkFontSize
                                    ? `${returnsDeeplinkFontSize}px`
                                    : undefined,
                                  textDecoration: "underline",
                                }}
                                url={order.returnDeeplink ?? "#"}
                              >
                                {t("Return items in this order")}
                              </ExternalLink>
                            )}
                          {showClaimsDeeplink &&
                            (order.claimDeeplink ||
                              environment ===
                                TrackingPageEnvironment.BUILDER) && (
                              <ExternalLink
                                style={{
                                  color: claimsDeeplinkTextColor || "inherit",
                                  fontSize: claimsDeeplinkFontSize
                                    ? `${claimsDeeplinkFontSize}px`
                                    : undefined,
                                  textDecoration: "underline",
                                }}
                                url={order.claimDeeplink ?? "#"}
                              >
                                {t("Start a claim")}
                              </ExternalLink>
                            )}
                        </Flex>
                      </Flex>
                      <Flex dir="column" gap="2xl" mt="md">
                        <IterableMap
                          items={thisPackageItems}
                          keyFn={(item) => item.id}
                        >
                          {(item, i) => (
                            <LineItem
                              badgeStyles={badgeStyles}
                              bodyStyles={bodyStyles}
                              customFields={customFields}
                              headerStyles={headerStyles}
                              hidePrice={hidePrice}
                              hideQuantity={hideQuantity}
                              imageAspectRatio={imageAspectRatio}
                              item={item}
                              lineItemPropertySettings={
                                lineItemPropertySettings
                              }
                              previewCustomFields={
                                environment === TrackingPageEnvironment.BUILDER
                              }
                              showCustomFields={showCustomFields}
                            />
                          )}
                        </IterableMap>
                      </Flex>
                    </Flex>
                  </Flex>
                );
              })()}
              <IterableMap items={order.shipments || []} keyFn={(s) => s.id}>
                {(shipment: TrackingPageShipment, i: number) => {
                  if (shipment.id === selectedShipment.id) {
                    return <div />;
                  }
                  // Skip (don't filter the array — `i` must stay aligned with
                  // order.shipments); these items show under "not yet shipped".
                  if (
                    shipment.status ===
                    OrderTrackingShipmentStatus.SHIPMENT_CANCELLED
                  ) {
                    return <div />;
                  }
                  const otherItems = visibleLineItems(
                    shipment?.lineItems ?? [],
                  );
                  if (otherItems.length === 0) {
                    return <div />;
                  }
                  return (
                    <Flex dir="column" gap="none" key={i} p="xl">
                      <Flex dir="column" gap="none" p="xl">
                        <Flex justify="space-between">
                          <Flex align="baseline" gap="sm">
                            <Text
                              fontSize="lg"
                              fontWeight="semibold"
                              style={headerStyles}
                            >
                              {t("Items in another package")}
                            </Text>
                            {itemCount(otherItems.length)}
                          </Flex>
                          {!order.cancelledAt && (
                            <RedoButton
                              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                                ...buttonStyles,
                                ...buttonBorderRadius,
                              }}
                              hierarchy={buttonHierarchy}
                              onClick={() => handleNavigate(i)}
                              size="xs"
                              text={t("Track Package")}
                            />
                          )}
                        </Flex>
                        <Flex dir="column" gap="2xl" mt="md">
                          <IterableMap
                            items={otherItems}
                            keyFn={(item) => item.id}
                          >
                            {(item, i) => (
                              <LineItem
                                badgeStyles={badgeStyles}
                                bodyStyles={bodyStyles}
                                customFields={customFields}
                                headerStyles={headerStyles}
                                hidePrice={hidePrice}
                                hideQuantity={hideQuantity}
                                imageAspectRatio={imageAspectRatio}
                                item={item}
                                lineItemPropertySettings={
                                  lineItemPropertySettings
                                }
                                previewCustomFields={
                                  environment ===
                                  TrackingPageEnvironment.BUILDER
                                }
                                showCustomFields={showCustomFields}
                              />
                            )}
                          </IterableMap>
                        </Flex>
                      </Flex>
                    </Flex>
                  );
                }}
              </IterableMap>
              {(() => {
                const unfulfilledItems = visibleLineItems(
                  getUnfulfilledLineItems(order),
                );
                if (unfulfilledItems.length === 0) {
                  return null;
                }
                return (
                  <Flex dir="column" gap="none" p="xl">
                    <Flex dir="column" gap="none" p="xl">
                      <Flex align="baseline" gap="sm">
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          style={headerStyles}
                        >
                          {t("Items not yet shipped")}
                        </Text>
                        {itemCount(unfulfilledItems.length)}
                      </Flex>
                      <Flex dir="column" gap="2xl" mt="md">
                        <IterableMap
                          items={unfulfilledItems}
                          keyFn={(item) => item.id}
                        >
                          {(item) => (
                            <LineItem
                              badgeStyles={badgeStyles}
                              bodyStyles={bodyStyles}
                              customFields={customFields}
                              headerStyles={headerStyles}
                              hidePrice={hidePrice}
                              hideQuantity={hideQuantity}
                              imageAspectRatio={imageAspectRatio}
                              item={item}
                              lineItemPropertySettings={
                                lineItemPropertySettings
                              }
                              previewCustomFields={
                                environment === TrackingPageEnvironment.BUILDER
                              }
                              showCustomFields={showCustomFields}
                            />
                          )}
                        </IterableMap>
                      </Flex>
                    </Flex>
                  </Flex>
                );
              })()}
            </>
          ) : (
            (() => {
              const orderItems = visibleLineItems(order.lineItems ?? []);
              if (orderItems.length === 0) {
                return null;
              }
              return (
                <Flex dir="column" gap="none" p="3xl">
                  <Flex align="center" justify="space-between">
                    <Flex align="baseline" gap="sm">
                      <Text
                        fontSize="lg"
                        fontWeight="semibold"
                        style={headerStyles}
                      >
                        {title ?? t("Items in this order")}
                      </Text>
                      {itemCount(orderItems.length)}
                    </Flex>
                    <Flex align="center" gap="md">
                      {showReturnsDeeplink &&
                        (order.returnDeeplink ||
                          environment === TrackingPageEnvironment.BUILDER) && (
                          <ExternalLink
                            style={{
                              color: returnsDeeplinkTextColor || "inherit",
                              fontSize: returnsDeeplinkFontSize
                                ? `${returnsDeeplinkFontSize}px`
                                : undefined,
                            }}
                            url={order.returnDeeplink ?? "#"}
                          >
                            {t("Return items in this order")}
                          </ExternalLink>
                        )}
                      {showClaimsDeeplink &&
                        (order.claimDeeplink ||
                          environment === TrackingPageEnvironment.BUILDER) && (
                          <ExternalLink
                            style={{
                              color: claimsDeeplinkTextColor || "inherit",
                              fontSize: claimsDeeplinkFontSize
                                ? `${claimsDeeplinkFontSize}px`
                                : undefined,
                            }}
                            url={order.claimDeeplink ?? "#"}
                          >
                            {t("Start a claim")}
                          </ExternalLink>
                        )}
                    </Flex>
                  </Flex>
                  <Flex dir="column" gap="2xl" mt="md">
                    <IterableMap items={orderItems} keyFn={(item) => item.id}>
                      {(item, i) => (
                        <LineItem
                          badgeStyles={badgeStyles}
                          bodyStyles={bodyStyles}
                          customFields={customFields}
                          headerStyles={headerStyles}
                          hidePrice={hidePrice}
                          hideQuantity={hideQuantity}
                          imageAspectRatio={imageAspectRatio}
                          item={item}
                          lineItemPropertySettings={lineItemPropertySettings}
                          previewCustomFields={
                            environment === TrackingPageEnvironment.BUILDER
                          }
                          showCustomFields={showCustomFields}
                        />
                      )}
                    </IterableMap>
                  </Flex>
                </Flex>
              );
            })()
          )}
        </Flex>
      )}
    </BlockWrapper>
  );
});

function toBlockOverflowCue(
  cue: ItemsOverflowCue,
  scrollbarColor: string | undefined,
): BlockOverflowCue | undefined {
  switch (cue) {
    case ItemsOverflowCue.SCROLLBAR_AND_FADE:
      return { scrollbar: true, fade: true, scrollbarColor };
    case ItemsOverflowCue.SCROLLBAR:
      return { scrollbar: true, fade: false, scrollbarColor };
    case ItemsOverflowCue.FADE:
      return { scrollbar: false, fade: true };
    case ItemsOverflowCue.NONE:
      return undefined;
  }
}
